import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import "./FileImportDialog.scss";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import FormField from "components/molecules/FormField/FormField";
import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import { ReactComponent as AddFile } from "assets/images/icons/AddFile.svg";
import { ReactComponent as DeleteIcon } from "assets/images/icons/Delete.svg";
import useDraggableUpload from "hooks/useDraggableUpload";
import usePermissions from "hooks/usePermissions";
import errorToast from "helpers/errorToast";
import { createFileImport } from "api/services/fileImport";
import { useSearchParams } from "react-router-dom";

interface FileImportDialogProps {
  onClose: () => void;
}

export default function FileImportDialog({ onClose }: FileImportDialogProps) {
  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit, getValues, setValue, control, watch } = methods;
  const { permissions } = usePermissions();
  const { admin_settings, quoting, off_platform } = JSON.parse(permissions || "{}")?.permissions || {};
  const selectedFile = useWatch({ control, name: "uploaded_file" });
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const selectedDataType = watch("data_type");

  const { mutate: createFileImportMutation, isLoading } = useMutation({
    mutationFn: (payload: FormData) => createFileImport(payload),
    onSuccess: (res: any) => {
      const importId = res.data?.file_import_job_id;
      if (importId) {
        searchParams.set("import", importId.toString());
        setSearchParams(searchParams);
      }
      queryClient.invalidateQueries({ queryKey: ["file-import", "list"], exact: false });
      onClose();
    },
    onError: (err) => errorToast(err),
  });

  const handleFileSelect = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      return;
    }
    setValue("uploaded_file", file);
  };
  const { dragging, ...draggableProps } = useDraggableUpload<HTMLDivElement>(handleFileSelect);

  const handleFileRemove = () => setValue("uploaded_file", null);

  const onSubmit = (data: any) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === "deactivate_existing") return;
      if (value === undefined || value === null) return;
      formData.append(key, value as any);
    });
    if (data.data_type === "rate_card") {
      formData.append("deactivate_existing", data.deactivate_existing ? "true" : "false");
    }
    createFileImportMutation(formData);
  };

  const handleDataTypeChange = (val: any) => {
    if ((val?.value === "off_platform" || val?.value === "rate_card") && methods.getValues("action") === "bulk_update") {
      methods.setValue("action", "bulk_create");
    }
    if (val?.value !== "rate_card") {
      methods.setValue("deactivate_existing", false);
    }
  };

  const isBulkCreateOnlyDataType = selectedDataType === "off_platform" || selectedDataType === "rate_card";

  return (
    <Dialog
      title={intl.formatMessage({
        id: "settings.fileImportTools.createNewImport",
        defaultMessage: "Create New Import",
      })}
      show
      onHide={onClose}
      size="sm"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Data & Action Dropdown */}
          <div className="d-flex">
            <div className="w-100">
              <label>
                <FormattedMessage id="settings.fileImportTools.createFileImport.selectData" defaultMessage="Select Data" />
              </label>
              <Dropdown
                name="data_type"
                options={[
                  ...(admin_settings?.perform_file_import && admin_settings?.create_view_edit_users
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "general.user",
                            defaultMessage: "User",
                          }),
                          value: "user",
                        },
                      ]
                    : []),
                  ...(admin_settings?.perform_file_import && !quoting?.restricted_to_address_book
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "general.address",
                            defaultMessage: "Address",
                          }),
                          value: "address",
                        },
                      ]
                    : []),
                  ...(admin_settings?.perform_file_import
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "general.contact",
                            defaultMessage: "Contact",
                          }),
                          value: "contact",
                        },
                      ]
                    : []),
                  ...(off_platform?.create_tracker
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "general.tracker",
                            defaultMessage: "Tracker",
                          }),
                          value: "off_platform",
                        },
                      ]
                    : []),
                  ...(admin_settings?.perform_rate_card_file_import
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "settings.fileImportTools.rateCards",
                            defaultMessage: "Rate Cards",
                          }),
                          value: "rate_card",
                        },
                      ]
                    : []),
                ]}
                placeholder={intl.formatMessage({
                  id: "settings.fileImportTools.dropdownPlaceholder",
                  defaultMessage: "Choose an option...",
                })}
                onChange={handleDataTypeChange}
                useHookForm
                required
              />
            </div>
            <div className="w-100 ml-3">
              <label>
                <FormattedMessage
                  id="settings.fileImportTools.createFileImport.selectAction"
                  defaultMessage="Select Action"
                />
              </label>
              <Dropdown
                name="action"
                placeholder={intl.formatMessage({
                  id: "settings.fileImportTools.dropdownPlaceholder",
                  defaultMessage: "Choose an option...",
                })}
                options={[
                  {
                    label: intl.formatMessage({
                      id: "settings.fileImportTools.createFileImport.bulkCreate",
                      defaultMessage: "Bulk create",
                    }),
                    value: "bulk_create",
                  },
                  ...(isBulkCreateOnlyDataType
                    ? []
                    : [
                        {
                          label: intl.formatMessage({
                            id: "settings.fileImportTools.createFileImport.bulkEdit",
                            defaultMessage: "Bulk edit",
                          }),
                          value: "bulk_update",
                        },
                      ]),
                ]}
                useHookForm
                required
              />
            </div>
          </div>
          {/* Description */}
          <div className="d-flex mt-4">
            <FormField
              field_id="description"
              label={intl.formatMessage({
                id: "general.description",
                defaultMessage: "Description",
              })}
              containerClasses="w-100"
              mandatory
            />
          </div>
          {selectedDataType === "rate_card" && (
            <div className="mt-4 ml-1 file-import-deactivate-checkbox">
              <Checkbox
                name="deactivate_existing"
                providerSections
                checkboxLabel={
                  <FormattedMessage
                    id="settings.fileImportTools.deactivateExisting"
                    defaultMessage="Deactivate existing rate cards"
                  />
                }
              />
            </div>
          )}
          {/* File Upload */}
          <div className="mt-4 file-import-container">
            <label className="required">
              <FormattedMessage
                id="settings.fileImportTools.createFileImport.fileUpload"
                defaultMessage="File upload (.xlsx)"
              />
            </label>
            {selectedFile && (
              <div className="d-flex">
                <p className="font-b4">{getValues("uploaded_file")?.name}</p>
                <DeleteIcon className="cursor-pointer ml-2" onClick={handleFileRemove} />
              </div>
            )}
            {!selectedFile && (
              <div className="uploader-container" {...draggableProps}>
                {!dragging ? (
                  <div className="d-flex flex-column align-items-center">
                    <AddFile />
                    <Button className="mt-3 position-relative" variant="secondary">
                      <FormattedMessage id="imports.uploadDialog.selectFile" defaultMessage="Select file" />
                      <input
                        type="file"
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="upload-input-file"
                        required
                        onChange={(e) => handleFileSelect(e.target.files)}
                      />
                    </Button>
                    <div className="text-primary mt-2">
                      <FormattedMessage
                        id="imports.uploadDialog.dragText"
                        defaultMessage="or just drag and drop in this area to upload"
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-primary h4" {...draggableProps}>
                    <FormattedMessage id="imports.uploadDialog.dropHere" defaultMessage="Drop here..." />
                  </p>
                )}
              </div>
            )}
          </div>
          {/* Footer */}
          <div className="d-flex mt-5">
            <Button
              variant="outline-primary"
              label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
              onClick={onClose}
            />
            <Button
              className="ml-auto"
              size="lg"
              variant="primary"
              type="submit"
              isLoading={isLoading}
              label={intl.formatMessage({ id: "general.confirm", defaultMessage: "Confirm" })}
            />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
}
